import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { marketingLogos, marketingNavigation, marketingPalette } from '../../content/marketing';
import { getCurrentUser } from '../../services/auth';
import api from '../../services/api';
import { getUserProfileUrl } from '../../utils/mediaUrl';
import useBillingVisibility from '../../hooks/useBillingVisibility';
import { isGlobalAdmin, isTenantScopedAdmin } from '../../utils/role';

const bodyFontFamily = '"Manrope", "Segoe UI", sans-serif';
const palette = marketingPalette;

function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { shouldShowBilling } = useBillingVisibility();
  const isAuthenticated = Boolean(localStorage.getItem('token'));
  const user = getCurrentUser();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) {
      setProfilePicture(null);
      return;
    }

    const fetchProfilePicture = async () => {
      try {
        const { data } = await api.get('/users/profile');
        setProfilePicture(data.basicInfo?.profilePictureUrl || data.basicInfo?.profilePicture || null);
      } catch (error) {
        console.error('Error fetching profile picture:', error);
      }
    };

    fetchProfilePicture();
  }, [isAuthenticated]);

  const getAuthenticatedPages = () => {
    const privatePages = [
      { title: 'Dashboard', path: '/dashboard' },
      { title: 'Cases', path: '/histories' },
      { title: 'Community Library', path: '/community-library' },
      { title: 'Case Manager', path: '/cases' },
    ];

    if (user?.accountType === 'individual' && shouldShowBilling) {
      privatePages.push({ title: 'Billing', path: '/account/billing' });
    }

    if (user?.accountType === 'university') {
      privatePages.push({ title: 'Cohorts', path: '/cohorts' });
      if (isTenantScopedAdmin(user)) {
        privatePages.push({ title: 'University Admin', path: '/university/admin' });
      }
    }

    return privatePages;
  };

  const menuOptions = useMemo(() => {
    if (!user) {
      return [];
    }

    const options = [
      { title: 'Profile', path: '/profile' },
      { title: 'Dashboard', path: '/dashboard' },
    ];

    if (user.accountType === 'individual' && shouldShowBilling) {
      options.splice(1, 0, { title: 'Billing', path: '/account/billing' });
    }

    if (user.accountType === 'university') {
      options.splice(1, 0, { title: 'Cohort Hub', path: '/cohorts' });
      if (isTenantScopedAdmin(user)) {
        options.splice(2, 0, { title: 'University Admin', path: '/university/admin' });
      }
    }

    if (isGlobalAdmin(user)) {
      options.push({ title: 'Platform Settings', path: '/platform-settings' });
      options.push({ title: 'Admin Panel', path: '/admin' });
    }

    options.push({ title: 'Logout', path: '/logout' });
    return options;
  }, [shouldShowBilling, user]);

  const desktopPages = isAuthenticated
    ? [...marketingNavigation, ...getAuthenticatedPages()]
    : marketingNavigation;

  const mobilePages = isAuthenticated
    ? [...marketingNavigation, ...getAuthenticatedPages()]
    : marketingNavigation;

  const isActiveRoute = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }

    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigate = (path) => {
    setMobileOpen(false);
    handleCloseUserMenu();

    if (path === '/logout') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
      return;
    }

    navigate(path);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: alpha('#f8fcff', 0.90),
        color: palette.ink,
        backdropFilter: 'blur(18px)',
        borderBottom: `1px solid ${alpha(palette.ink, 0.08)}`,
      }}
    >
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
        <Toolbar disableGutters sx={{ minHeight: { xs: 74, md: 82 }, gap: 2 }}>
          <Box
            component={Link}
            to="/"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={marketingLogos.compactDark}
              alt="Virtual Medic"
              sx={{
                width: { xs: 126, sm: 148 },
                maxWidth: '100%',
              }}
            />
          </Box>

          <Box
            sx={{
              display: { xs: 'none', lg: 'flex' },
              alignItems: 'center',
              gap: 0.75,
              flex: 1,
              minWidth: 0,
              ml: 1,
            }}
          >
            {desktopPages.map((page) => (
              <Button
                key={`${page.title}-${page.path}`}
                onClick={() => handleNavigate(page.path)}
                sx={{
                  px: 1.65,
                  py: 1.15,
                  minWidth: 'auto',
                  borderRadius: '999px',
                  color: isActiveRoute(page.path) ? palette.ink : palette.inkSoft,
                  bgcolor: isActiveRoute(page.path) ? alpha(palette.heroBlue, 0.14) : 'transparent',
                  fontSize: '0.92rem',
                  fontWeight: isActiveRoute(page.path) ? 800 : 700,
                  textTransform: 'none',
                  fontFamily: bodyFontFamily,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: alpha(palette.heroBlue, 0.10),
                  },
                }}
              >
                {page.title}
              </Button>
            ))}
          </Box>

          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ ml: 'auto', flexShrink: 0 }}
          >
            {isAuthenticated ? (
              <>
                <IconButton
                  onClick={(event) => setAnchorElUser(event.currentTarget)}
                  sx={{ p: 0 }}
                >
                  <Tooltip title="Open account menu">
                    <Avatar
                      alt={user?.firstName || 'User'}
                      src={getUserProfileUrl(profilePicture)}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: palette.heroBlueDeep,
                        fontFamily: bodyFontFamily,
                        fontWeight: 800,
                      }}
                    >
                      {!profilePicture && (user?.firstName?.charAt(0)?.toUpperCase() || 'U')}
                    </Avatar>
                  </Tooltip>
                </IconButton>

                <Menu
                  anchorEl={anchorElUser}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      borderRadius: '18px',
                      minWidth: 220,
                      border: `1px solid ${alpha(palette.ink, 0.08)}`,
                      boxShadow: '0 18px 48px rgba(18, 61, 103, 0.12)',
                    },
                  }}
                >
                  {menuOptions.map((item) => (
                    <MenuItem
                      key={item.title}
                      onClick={() => handleNavigate(item.path)}
                      sx={{
                        py: 1.35,
                        fontFamily: bodyFontFamily,
                        fontWeight: item.path === '/logout' ? 800 : 700,
                      }}
                    >
                      {item.title}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.25 }}>
                <Button
                  component={Link}
                  to="/login"
                  sx={{
                    px: 1.5,
                    color: palette.ink,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontFamily: bodyFontFamily,
                    '&:hover': {
                      bgcolor: alpha(palette.heroBlue, 0.08),
                    },
                  }}
                >
                  Log in
                </Button>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  sx={{
                    minHeight: 46,
                    px: 2.5,
                    bgcolor: palette.heroBlueDeep,
                    color: palette.white,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontFamily: bodyFontFamily,
                    borderRadius: '999px',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: palette.heroBlueDeeper,
                      boxShadow: 'none',
                    },
                  }}
                >
                  Get started
                </Button>
              </Box>
            )}

            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{
                display: { xs: 'inline-flex', lg: 'none' },
                width: 42,
                height: 42,
                border: `1px solid ${alpha(palette.ink, 0.08)}`,
                bgcolor: alpha('#ffffff', 0.82),
              }}
            >
              <MenuIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </Container>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 'min(340px, 90vw)',
            p: 2.25,
            bgcolor: '#f6fbff',
          },
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2.5 }}>
          <Box
            component="img"
            src={marketingLogos.compactDark}
            alt="Virtual Medic"
            sx={{ width: 134, maxWidth: '100%' }}
          />
          <IconButton onClick={() => setMobileOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Typography
          sx={{
            mb: 1.25,
            color: palette.heroBlueMid,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.78rem',
          }}
        >
          Navigation
        </Typography>

        <List sx={{ p: 0 }}>
          {mobilePages.map((page) => (
            <ListItemButton
              key={`${page.title}-${page.path}`}
              onClick={() => handleNavigate(page.path)}
              sx={{
                mb: 0.75,
                borderRadius: '18px',
                py: 1.45,
                px: 1.5,
                bgcolor: isActiveRoute(page.path) ? alpha(palette.heroBlue, 0.14) : 'transparent',
              }}
            >
              <Typography sx={{ fontWeight: 800, fontFamily: bodyFontFamily }}>
                {page.title}
              </Typography>
            </ListItemButton>
          ))}
        </List>

        <Divider sx={{ my: 2.25, borderColor: alpha(palette.ink, 0.08) }} />

        {isAuthenticated ? (
          <>
            <Typography
              sx={{
                mb: 1.25,
                color: palette.heroBlueMid,
                fontWeight: 800,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontSize: '0.78rem',
              }}
            >
              Account
            </Typography>
            <List sx={{ p: 0 }}>
              {menuOptions.map((item) => (
                <ListItemButton
                  key={item.title}
                  onClick={() => handleNavigate(item.path)}
                  sx={{
                    mb: 0.75,
                    borderRadius: '18px',
                    py: 1.45,
                    px: 1.5,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontFamily: bodyFontFamily }}>
                    {item.title}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </>
        ) : (
          <Stack spacing={1.5}>
            <Button
              component={Link}
              to="/signup"
              variant="contained"
              onClick={() => setMobileOpen(false)}
              sx={{
                minHeight: 50,
                bgcolor: palette.heroBlueDeep,
                color: palette.white,
                textTransform: 'none',
                fontWeight: 800,
                fontFamily: bodyFontFamily,
                borderRadius: '999px',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: palette.heroBlueDeeper,
                  boxShadow: 'none',
                },
              }}
            >
              Get started
            </Button>
            <Button
              component={Link}
              to="/login"
              onClick={() => setMobileOpen(false)}
              variant="outlined"
              sx={{
                minHeight: 50,
                color: palette.heroBlueDeep,
                textTransform: 'none',
                fontWeight: 700,
                fontFamily: bodyFontFamily,
                borderRadius: '999px',
                borderColor: alpha(palette.heroBlueDeep, 0.16),
                '&:hover': {
                  borderColor: alpha(palette.heroBlueDeep, 0.36),
                  bgcolor: alpha(palette.heroBlueDeep, 0.03),
                },
              }}
            >
              Log in
            </Button>
          </Stack>
        )}
      </Drawer>
    </AppBar>
  );
}

export default Navbar;
